import { useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

/**
 * Robust QR scanner hook that prevents the removeChild DOM error.
 * 
 * The html5-qrcode library creates internal DOM elements (video, canvas) inside
 * a container. When React unmounts the container before the scanner is properly
 * stopped, a removeChild error occurs. This hook ensures proper cleanup order:
 * 1. Stop scanning (releases camera)
 * 2. Clear internal DOM elements (removes video/canvas)
 * 3. Only then allow React to unmount the container
 */
export function useQrScanner() {
  const scannerRef = useRef(null);
  const busyRef = useRef(false);

  const stop = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (scanner) {
      try {
        if (scanner.isScanning) await scanner.stop();
      } catch {}
      try {
        scanner.clear();
      } catch {}
    }
    busyRef.current = false;
  }, []);

  const start = useCallback(async (elementId, onSuccess, config = {}) => {
    await stop();
    const el = document.getElementById(elementId);
    if (!el) throw new Error('Scanner container not found');
    el.innerHTML = '';
    const innerId = `${elementId}-inner-${Date.now()}`;
    const inner = document.createElement('div');
    inner.id = innerId;
    el.appendChild(inner);
    const scanner = new Html5Qrcode(innerId);
    scannerRef.current = scanner;
    await scanner.start(
      { facingMode: 'environment' },
      { fps: config.fps || 10, qrbox: config.qrbox || { width: 220, height: 220 }, ...config },
      (text) => onSuccess(text),
      () => {}
    );
    return scanner;
  }, [stop]);

  return { start, stop, scannerRef, busyRef };
}
